import React, { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  FormLabelGroup,
  Input
} from '@appfolio/react-gears';
import { toPath, withEnvRoot } from '@tportal/common/utils';
import { signIn } from '@tportal/login/api/sessions';
import CompanyInfoFooter from '@tportal/login/components/CompanyInfoFooter';
import CompanyLogo from '@tportal/login/components/CompanyLogo';
import ShowPasswordInput from '@tportal/login/components/ShowPasswordInput';
import { useAlert } from '@tportal/login/hooks/use_alert';
import i18n from '@tportal/login/i18n';
import { User } from '@tportal/login/types';

interface Props {
  isHOA: boolean;
  logoUrl?: string;
  pmInfo: any;
  selfSignupEnabled?: boolean;
}

const appleStore = {
  badgeUrl: 'https://publicdevelopment.cdn.appfolio.com/public/images/badges/apple_store_badge.svg',
  appUrl: 'https://itunes.apple.com/us/app/online-portal-by-appfolio/id1376350358?mt=8',
  badgeStyle: { width: '120px' }
};

const googleStore = {
  badgeUrl: 'https://publicdevelopment.cdn.appfolio.com/public/images/badges/google_store_badge.svg',
  appUrl: 'https://play.google.com/store/apps/details?id=com.appfolio.resident_portal&utm_source=portal_login',
  badgeStyle: { width: '150px' }
};

const LoginPage = (props: Props) => {
  const {
    isHOA,
    logoUrl,
    pmInfo,
    selfSignupEnabled,
  } = props;

  const { alert, setAlert } = useAlert();

  const containerClass = (
    `d-flex flex-column h-100 w-100 p-0 m-0 top-0 start-0 overflow-auto${isMobile ? '' : ' pt-5'}`
  );
  const cardClass = isMobile ? 'start-0 top-0 w-100 m-0 align-items-center py-4' :
    'align-items-center justify-content-center py-4 font-weight-light start-50 translate-middle-x';
  const cardBodyClass = isMobile ? 'px-4 w-100' : 'mx-3';

  const history = useHistory();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (user: User): Promise<void> => {
    setLoading(true);
    try {
      const {
        passwordAccepted,
        redirectUrl
      } = await signIn(user);

      if (passwordAccepted && redirectUrl) {
        history.replace(toPath(redirectUrl));
      } else if (redirectUrl) {
        history.replace(toPath(redirectUrl));
      } else {
        setLoading(false);
        setAlert({ type: 'danger', message: i18n.t('errors.invalid_login') });
      }
    } catch (e: any) {
      const err = await e.json();
      setAlert({ type: 'danger', message: err.error ?? i18n.t('errors.invalid_login') });
      setLoading(false);
    }
  };

  const handleKeyDown = async (event: any) => {
    if (event.key === 'Enter') {
      await handleSignIn({ email, password });
    }
  };

  return (
    <div className='login-page__body top-0 start-0 w-100 m-0 p-0 h-100'>
      <div className='login-page__body'>
        <div className={containerClass}>
          <Card className={cardClass} style={isMobile ? { width: window.innerWidth } : { width: '400px' }}>
            <CompanyLogo
              companyName={pmInfo.name}
              logoUrl={logoUrl}
            />
            <h2
              className='mt-3 mb-0 font-weight-light u-text-center'
              aria-label='Online Portal Log In'
            >
              {i18n.t('labels.online_portal')}
            </h2>
            <CardBody className={cardBodyClass}>
              {alert &&
                <Alert color={alert.type} icon>
                  {alert.message}
                </Alert>
              }
              <FormLabelGroup
                className='d-flex justify-content-center'
                label={i18n.t('labels.email')}
                stacked
              >
                <Input
                  type='email'
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  aria-label='Enter your email address'
                  data-testid='email-input'
                />
              </FormLabelGroup>
              <FormLabelGroup
                className='d-flex pt-1 justify-content-center'
                label={i18n.t('labels.password')}
                stacked
              >
                <ShowPasswordInput
                  password={password}
                  setPassword={setPassword}
                />
              </FormLabelGroup>
              <Button
                block
                className='js-sign-in-react mt-2 font-weight-light'
                color='primary'
                disabled={loading}
                onClick={async () => handleSignIn({ email, password })}
                onKeyDown={handleKeyDown}
              >
                {i18n.t('actions.sign_in')}
              </Button>
            </CardBody>
            <a
              href={withEnvRoot('/users/password/new')}
              className='js-reset-password mb-3 mt-1'
              aria-label='Reset your password'
            >
              {i18n.t('actions.forgot_password')}
            </a>
            {selfSignupEnabled ?
              <a
                className='js-request-access'
                href={withEnvRoot('/users/request_access')}
                aria-label='Interested in signing up for online access? Please contact your property manager.'
              >
                {i18n.t('actions.need_to_setup_account')}
              </a>
              :
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <a
                className='text-center'
                aria-label='Do you need to setup an account?'
              >
                {i18n.t('actions.request_online_access')}
              </a>
            }
          </Card>
          <div className='align-items-center justify-content-center py-3 d-flex'>
            <a
              className='js-app-store'
              href={appleStore.appUrl}
              aria-label='Download the app from the App Store'
              data-testid='apple-store-link'
            >
              <img
                src={appleStore.badgeUrl}
                alt='Apple Store Badge'
                className='ms-2 me-3'
                style={appleStore.badgeStyle}
                data-testid='apple-store-badge'
              />
            </a>
            <a
              className='js-google-play-store'
              href={googleStore.appUrl}
              aria-label='Download the app from Google Play'
              data-testid='google-store-link'
            >
              <img
                src={googleStore.badgeUrl}
                alt='Google Play Badge'
                style={googleStore.badgeStyle}
                data-testid='google-store-badge'
              />
            </a>
          </div>
          <CompanyInfoFooter pmInfo={pmInfo} isHOA={isHOA} />
        </div>
      </div>
    </div>
  );
};

export default observer(LoginPage);
